import { finalize, takeUntil } from "rxjs/operators";
import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  QueryList,
  ViewChildren,
  AfterViewInit,
  ElementRef
} from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { State, getUserType } from "../../reducers";
import { SHOW_SNACKBAR } from "../../reducers/actions/snackbar";
import { Statement } from "../statement";
import { SubscribingComponent, SnackbarConfig } from "../../common";
import { StatementService } from "../statement.service";
import { HideViewSpinnerAction, ShowViewSpinnerAction } from "app/reducers/actions/loader";
import { FirebaseEventsService } from "app/common/firebase-events.service";
import { WorkflowControllerService } from "app/core/workflow/workflow-controller/workflow-controller.service";
import { PDFDocument } from "pdf-lib";
import { SelectButton } from "app/common/select-buttons/select-buttons.component";

interface PremiumIncomeFileResponse {
  id?: number;
  file?: string;
  original_filename?: string;
}

interface PremiumIncomeEntryResponse {
  id?: number;
  name?: string;
  files?: PremiumIncomeFileResponse[];
}

interface PremiumIncomeGroupResponse {
  id?: number;
  income_type: PremiumIncomeType;
  additional_information?: string;
  entries?: PremiumIncomeEntryResponse[];
}

type PremiumIncomeType = "real_estate" | "capital_income";

interface PremiumIncomePayloadFile {
  id?: number;
  file?: File;
}

interface PremiumIncomePayloadEntry {
  id?: number;
  name: string;
  files: PremiumIncomePayloadFile[];
}

interface PremiumIncomePayloadGroup {
  income_type: PremiumIncomeType;
  additional_information?: string;
  entries: PremiumIncomePayloadEntry[];
}

interface PremiumIncomePayload {
  premium_income: PremiumIncomePayloadGroup[];
  stage_steuer_additional_options: boolean;
  stage_employers: boolean;
  has_rental_income: boolean;
  has_capital_income: boolean;
}

interface ExistingPremiumFile {
  id: number;
  file: string;
  original_filename: string;
}

interface NewPremiumFile {
  file: File;
}

interface PremiumIncomeEntryForm {
  id?: number;
  name: string;
  existingFiles: ExistingPremiumFile[];
  newFiles: NewPremiumFile[];
}

@Component({
  selector: "app-additional-income-options",
  templateUrl: "./additional-income-options.component.html",
  styleUrls: ["./additional-income-options.component.scss"],
  standalone: false
})
export class AdditionalIncomeOptionsComponent extends SubscribingComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() isNested = false;
  @Input() nestedStatement: Statement;
  @Input() contentLoaded = false;

  @ViewChildren("entryTitle") entryTitles: QueryList<ElementRef<HTMLElement>>;

  public errors: any[] = [];
  public statementId: number;
  public spouseId = 0;
  public statement: Statement;
  public loadingStatementData = true;
  public isBusiness = false;
  public selectedIncomeTypes: PremiumIncomeType[] = [];
  private lastResolvedIncomeTypes: PremiumIncomeType[] = [];
  private readonly selectedIncomeTypesStoragePrefix = "additional-income-options-selected-types";
  public readonly premiumIncomeSelectionButtons: SelectButton[] = [
    {
      currentId: "real_estate",
      name: "real_estate",
      label: "ASIDE_BAR.PREMIUM_INCOME_SUBLIST.REAL_ESTATE",
      isSelected: () => this.isIncomeSelected("real_estate"),
      imageSrc: "assets/img/add_options/icon_1_dodatkowe.png",
      clickAction: () => this.toggleIncomeSelection("real_estate")
    },
    {
      currentId: "capital_income",
      name: "capital_income",
      label: "ASIDE_BAR.PREMIUM_INCOME_SUBLIST.CAPITAL_INCOME",
      isSelected: () => this.isIncomeSelected("capital_income"),
      imageSrc: "assets/img/add_options/icon_2_dodatkowe.png",
      clickAction: () => this.toggleIncomeSelection("capital_income")
    }
  ];

  public buttons = [
    {
      type: "back",
      label: "COMMON.BACK",
      action: this.goBack.bind(this)
    },
    {
      type: "proceed",
      label: "COMMON.PROCEED",
      action: this.handlePrimaryButton.bind(this)
    }
  ];

  public currentStep = 1;
  public readonly maxEntriesNumber = 10;
  public readonly maxFilesNumberPerEntry = 10;
  public readonly maxFileNameLength = 100;
  public readonly maxFileSizeMb = 10;
  public readonly maxFilePages = 100;

  public additionalInformation = "";
  public entries: PremiumIncomeEntryForm[] = [];

  constructor(
    private store: Store<State>,
    private statementService: StatementService,
    private router: Router,
    private route: ActivatedRoute,
    private firebaseEventsService: FirebaseEventsService,
    public workFlowController: WorkflowControllerService
  ) {
    super();
  }

  ngOnInit() {
    this.store.dispatch(new ShowViewSpinnerAction());

    this.route.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe((params) => {
      this.statementId = parseInt(params["statementId"], 10);
      this.spouseId = params["spouseId"] ? parseInt(params["spouseId"], 10) : 0;
      const routeStep = params["step"];
      this.currentStep = this.normalizeStep(routeStep);

      if (!routeStep && this.statementId) {
        this.router.navigate([this.stepRoute(1)], { replaceUrl: true });
        return;
      }

      if (routeStep && Number(routeStep) !== this.currentStep && this.statementId) {
        this.router.navigate([this.stepRoute(this.currentStep)], {
          replaceUrl: true
        });
        return;
      }

      if (this.nestedStatement) {
        this.statement = this.nestedStatement;
        this.workFlowController.init(this.statement);
        this.loadingStatementData = false;
        this.store.dispatch(new HideViewSpinnerAction());
        this.initFormFromStatement();
      } else {
        this.getStatementData(this.statementId);
      }
    });

    this.store
      .select(getUserType)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((userType: string) => {
        if (userType) {
          this.isBusiness = userType === "business";
        }
      });
  }

  ngOnChanges(changes: SimpleChanges) {}

  ngAfterViewInit() {
    this.entryTitles.changes.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.applyLongTitleClass();
    });
  }

  public get isSelectionStep(): boolean {
    return this.currentStep === 1;
  }

  public get currentIncomeType(): PremiumIncomeType {
    return this.getSelectedIncomeTypes()[this.currentStep - 2] || this.getSelectedIncomeTypes()[0] || "real_estate";
  }

  public get titleKey(): string {
    if (this.isSelectionStep) {
      return "PREMIUM_INCOME.TITLE";
    }

    return this.currentIncomeType === "real_estate"
      ? "PREMIUM_INCOME.REAL_ESTATE.TITLE"
      : "PREMIUM_INCOME.CAPITAL_INCOME.TITLE";
  }

  public get descriptionKey(): string {
    if (this.isSelectionStep) {
      return "PREMIUM_INCOME.DESCRIPTION";
    }

    return this.currentIncomeType === "real_estate"
      ? "PREMIUM_INCOME.REAL_ESTATE.DESCRIPTION"
      : "PREMIUM_INCOME.CAPITAL_INCOME.DESCRIPTION";
  }

  public get shouldShowDescriptionTooltip(): boolean {
    return !this.isSelectionStep;
  }

  public get descriptionTooltipTitleKey(): string {
    return this.currentIncomeType === "real_estate"
      ? "PREMIUM_INCOME.REAL_ESTATE.TOOLTIP_TITLE"
      : "PREMIUM_INCOME.CAPITAL_INCOME.TOOLTIP_TITLE";
  }

  public get descriptionTooltipMessageKey(): string {
    return this.currentIncomeType === "real_estate"
      ? "PREMIUM_INCOME.REAL_ESTATE.TOOLTIP_MESSAGE"
      : "PREMIUM_INCOME.CAPITAL_INCOME.TOOLTIP_MESSAGE";
  }

  public get infoKey(): string {
    return this.currentIncomeType === "real_estate"
      ? "PREMIUM_INCOME.REAL_ESTATE.INFO"
      : "PREMIUM_INCOME.CAPITAL_INCOME.INFO";
  }

  public get addButtonKey(): string {
    return this.currentIncomeType === "real_estate"
      ? "PREMIUM_INCOME.REAL_ESTATE.ADD_ENTRY"
      : "PREMIUM_INCOME.CAPITAL_INCOME.ADD_ENTRY";
  }

  public get sectionLabelKey(): string {
    return this.currentIncomeType === "real_estate"
      ? "PREMIUM_INCOME.REAL_ESTATE.SECTION_LABEL"
      : "PREMIUM_INCOME.CAPITAL_INCOME.SECTION_LABEL";
  }

  public get nameLabelKey(): string {
    return this.currentIncomeType === "real_estate"
      ? "PREMIUM_INCOME.REAL_ESTATE.NAME_LABEL"
      : "PREMIUM_INCOME.CAPITAL_INCOME.NAME_LABEL";
  }

  public get namePlaceholderKey(): string {
    return this.currentIncomeType === "real_estate"
      ? "PREMIUM_INCOME.REAL_ESTATE.NAME_PLACEHOLDER"
      : "PREMIUM_INCOME.CAPITAL_INCOME.NAME_PLACEHOLDER";
  }

  public get additionalInfoLabelKey(): string {
    return this.currentIncomeType === "real_estate"
      ? "PREMIUM_INCOME.REAL_ESTATE.ADDITIONAL_INFO_LABEL"
      : "PREMIUM_INCOME.CAPITAL_INCOME.ADDITIONAL_INFO_LABEL";
  }

  public get additionalInfoPlaceholderKey(): string {
    return this.currentIncomeType === "real_estate"
      ? "PREMIUM_INCOME.REAL_ESTATE.ADDITIONAL_INFO_PLACEHOLDER"
      : "PREMIUM_INCOME.CAPITAL_INCOME.ADDITIONAL_INFO_PLACEHOLDER";
  }

  public goBack() {
    this.save(false, true);
  }

  public proceed() {
    this.handlePrimaryButton();
  }

  public handlePrimaryButton(): void {
    if (this.isSelectionStep) {
      const selectedTypes = this.getSelectedIncomeTypes();
      this.firebaseEventsService.logEvent(
        "click_add_extra_options",
        {
          premium_income_type: selectedTypes.length ? selectedTypes.join(",") : "skip",
          entries_count: this.entries.length,
          step: "selection"
        },
        this.statementId
      );

      this.save(true, false);
      return;
    }

    this.firebaseEventsService.logEvent(
      "click_add_extra_options",
      { premium_income_type: this.currentIncomeType, entries_count: this.entries.length, step: this.currentStep },
      this.statementId
    );

    this.save(true, false);
  }

  public toggleIncomeSelection(incomeType: PremiumIncomeType): void {
    if (this.selectedIncomeTypes.includes(incomeType)) {
      this.selectedIncomeTypes = this.selectedIncomeTypes.filter((item) => item !== incomeType);
    } else {
      this.selectedIncomeTypes = [...this.selectedIncomeTypes, incomeType];
    }

    this.syncToolbarButtons();
  }

  public isIncomeSelected(incomeType: PremiumIncomeType): boolean {
    return this.selectedIncomeTypes.includes(incomeType);
  }

  public addEntry(): void {
    if (this.entries.length >= this.maxEntriesNumber) {
      this.showErrorSnackbar("PREMIUM_INCOME.ERROR.MAX_ENTRIES");
      return;
    }

    this.entries.push(this.createEmptyEntry());
  }

  public removeEntry(index: number): void {
    this.entries.splice(index, 1);

    if (this.entries.length === 0) {
      this.entries.push(this.createEmptyEntry());
    }
  }

  public removeExistingFile(entry: PremiumIncomeEntryForm, fileIndex: number): void {
    entry.existingFiles.splice(fileIndex, 1);
  }

  public removeNewFile(entry: PremiumIncomeEntryForm, fileIndex: number): void {
    entry.newFiles.splice(fileIndex, 1);
  }

  public async onFilesSelected(event: Event, entry: PremiumIncomeEntryForm): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const files = Array.from(input.files);
    const usedSlots = entry.existingFiles.length + entry.newFiles.length;

    if (usedSlots + files.length > this.maxFilesNumberPerEntry) {
      this.showErrorSnackbar("PREMIUM_INCOME.ERROR.MAX_FILES");
      input.value = "";
      return;
    }

    for (const file of files) {
      const validationErrorKey = await this.validateFile(file);
      if (validationErrorKey) {
        this.showErrorSnackbar(validationErrorKey);
        continue;
      }

      entry.newFiles.push({ file });
    }

    input.value = "";
  }

  public getExistingFileName(file: ExistingPremiumFile): string {
    if (file.original_filename) return file.original_filename;

    const source = file.file || "";
    const chunks = source.split("/");
    return chunks.length ? chunks[chunks.length - 1] : "plik.pdf";
  }

  public updateStatement(goForward = false, goBack = false) {
    this.contentLoaded = false;
    this.errors = [];
    const selectedTypesSnapshot = this.getSelectedIncomeTypes().slice();
    const currentStepSnapshot = this.currentStep;
    this.persistSelectedIncomeTypes(selectedTypesSnapshot);
    const nextStep = goForward ? this.getNextRouteStep(selectedTypesSnapshot, currentStepSnapshot) : undefined;

    const payload = this.buildPayload();
    const requestBody = this.createRequestBody(payload);

    this.statementService
      .updateStatement(this.statement.id, requestBody)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.contentLoaded = true;
        })
      )
      .subscribe(
          (response: Statement) => {
          Object.assign(this.statement, response);
          this.restoreSelectedIncomeTypesIntoStatement(this.statement);
          this.normalizeSpouseFromResponse();
          this.initFormFromStatement();

          const previousStep = goBack ? this.getPreviousRouteStep() : undefined;

          if (typeof nextStep === "number") {
            this.router.navigate([this.stepRoute(nextStep)]);
            return;
          }

          if (typeof previousStep === "number") {
            this.router.navigate([this.stepRoute(previousStep)]);
            return;
          }

          if (goForward) {
            this.workFlowController.goToTheNextStep();
          }

          if (goBack) {
            this.workFlowController.goToThePreviousStep();
          }
        },
        (error: any) => {
          const serverError = error?.error;
          const premiumErrors = this.extractPremiumErrors(serverError);

          if (premiumErrors.length > 0) {
            this.showErrorSnackbar(premiumErrors[0]);
            return;
          }

          this.showErrorSnackbar("PREMIUM_INCOME.ERROR.GENERAL");
        }
      );
  }

  private save(goForward = false, goBack = false): void {
    this.updateStatement(goForward, goBack);
  }

  private getStatementData(id: number) {
    this.loadingStatementData = true;
    this.statementService
      .getStatement(id)
      .pipe(
        takeUntil(this.ngUnsubscribe),
        finalize(() => {
          this.store.dispatch(new HideViewSpinnerAction());
          this.loadingStatementData = false;
        })
      )
      .subscribe(
        (response: Statement) => {
          this.statement = response;
          this.restoreSelectedIncomeTypesIntoStatement(this.statement);
          this.normalizeSpouseFromResponse();
          this.workFlowController.init(this.statement);
          this.contentLoaded = true;
          this.initFormFromStatement();
        },
        () => {
          this.router.navigate(["/new"]);
        }
      );
  }

  private normalizeStep(rawStep: string | number | undefined): number {
    const parsed = Number(rawStep || 1);
    if (parsed >= 1 && parsed <= 3) return parsed;
    return 1;
  }

  private initFormFromStatement(): void {
    const premiumIncomeGroups: PremiumIncomeGroupResponse[] = Array.isArray((this.activeStatement as any)?.premiumIncome)
      ? (this.activeStatement as any).premiumIncome
      : [];

    this.syncSelectionFromStatement(premiumIncomeGroups);

    if (!this.isSelectionStep) {
      const selectedTypes = this.getSelectedIncomeTypes();
      const currentType = selectedTypes[this.currentStep - 2];

      if (!currentType) {
        const targetStep = selectedTypes.length > 0 ? 2 : 1;
        if (targetStep !== this.currentStep) {
          this.router.navigate([this.stepRoute(targetStep)], { replaceUrl: true });
        }
        return;
      }
    }

    if (this.isSelectionStep) {
      this.additionalInformation = "";
      this.entries = [];
      this.syncToolbarButtons();
      return;
    }

    const currentGroup = premiumIncomeGroups.find((item) => item?.income_type === this.currentIncomeType);

    this.additionalInformation = currentGroup?.additional_information || "";

    const responseEntries = Array.isArray(currentGroup?.entries) ? currentGroup.entries : [];
    this.entries = responseEntries.map((entry) => this.mapEntryToForm(entry));

    if (this.entries.length === 0) {
      this.entries = [this.createEmptyEntry()];
    }

    this.syncToolbarButtons();
    setTimeout(() => this.applyLongTitleClass());
  }

  private mapEntryToForm(entry: PremiumIncomeEntryResponse): PremiumIncomeEntryForm {
    const existingFiles = (Array.isArray(entry?.files) ? entry.files : [])
      .filter((file) => !!file?.id)
      .map((file) => ({
        id: file.id,
        file: file.file || "",
        original_filename: file.original_filename || ""
      }));

    return {
      id: entry?.id,
      name: entry?.name || "",
      existingFiles,
      newFiles: []
    };
  }

  private createEmptyEntry(): PremiumIncomeEntryForm {
    return {
      name: "",
      existingFiles: [],
      newFiles: []
    };
  }

  private buildPayload(): PremiumIncomePayload {
    const allGroups: PremiumIncomeGroupResponse[] = Array.isArray((this.activeStatement as any)?.premiumIncome)
      ? JSON.parse(JSON.stringify((this.activeStatement as any).premiumIncome))
      : [];

    const selectedTypes = this.getSelectedIncomeTypes();
    const selectedTypeSet = new Set<PremiumIncomeType>(selectedTypes);

    const cleanedCurrentEntries = this.isSelectionStep
      ? []
      : this.entries
          .filter((entry) => this.entryHasAnyData(entry))
          .map((entry) => {
            const files: PremiumIncomePayloadFile[] = [];

            entry.existingFiles.forEach((file) => {
              files.push({ id: file.id });
            });

            entry.newFiles.forEach((file) => {
              files.push({ file: file.file });
            });

            const item: PremiumIncomePayloadEntry = {
              name: this.normalizeEntryName(entry.name),
              files
            };

            if (entry.id) {
              item.id = entry.id;
            }

            return item;
          });

    const normalizedGroups: PremiumIncomePayloadGroup[] = allGroups
      .filter((group) => selectedTypeSet.has(group?.income_type) && (this.isSelectionStep || group?.income_type !== this.currentIncomeType))
      .map((group) => this.mapResponseGroupToPayload(group));

    const shouldPersistCurrentGroup = !this.isSelectionStep && selectedTypeSet.has(this.currentIncomeType);

    if (shouldPersistCurrentGroup) {
      normalizedGroups.push({
        income_type: this.currentIncomeType,
        additional_information: this.additionalInformation?.trim() || "",
        entries: cleanedCurrentEntries
      });
    }

    return {
      premium_income: normalizedGroups,
      stage_steuer_additional_options: true,
      stage_employers: true,
      has_rental_income: selectedTypeSet.has("real_estate"),
      has_capital_income: selectedTypeSet.has("capital_income")
    };
  }

  private createRequestBody(payload: PremiumIncomePayload): FormData | any {
    const hasBinaryFiles = payload.premium_income.some((group: any) =>
      (group.entries || []).some((entry: any) => (entry.files || []).some((file: any) => file.file instanceof File))
    );
    const hasSpouse = !!this.statement?.spouse;

    if (!hasBinaryFiles) {
      if (this.isSpouseMode) {
        return {
          spouses: [this.spousePayload(payload)]
        };
      }
      const response: any = {
        stage_steuer_additional_options: payload.stage_steuer_additional_options,
        stage_employers: payload.stage_employers,
        has_rental_income: payload.has_rental_income,
        has_capital_income: payload.has_capital_income,
        premium_income: payload.premium_income
      };
      if (hasSpouse) {
        response.spouses = [this.spouseBasePayload()];
      }
      return response;
    }

    const formData = new FormData();

    if (this.isSpouseMode) {
      this.appendSpouseBaseFields(formData, payload);
    } else {
      formData.append("stage_steuer_additional_options", String(payload.stage_steuer_additional_options));
      formData.append("stage_employers", String(payload.stage_employers));
      formData.append("has_rental_income", String(payload.has_rental_income));
      formData.append("has_capital_income", String(payload.has_capital_income));
      if (hasSpouse) {
        this.appendSpouseBaseFields(formData, payload);
      }
    }

    if (payload.premium_income.length === 0) {
      if (this.isSpouseMode) {
        formData.append("spouses[0][premium_income]", "[]");
      } else {
        formData.append("premium_income", "[]");
      }
      return formData;
    }

    payload.premium_income.forEach((group, groupIndex) => {
      const groupPath = this.isSpouseMode
        ? `spouses[0][premium_income][${groupIndex}]`
        : `premium_income[${groupIndex}]`;

      formData.append(`${groupPath}[income_type]`, group.income_type);
      formData.append(`${groupPath}[additional_information]`, group.additional_information || "");

      const entries = Array.isArray(group.entries) ? group.entries : [];

      entries.forEach((entry, entryIndex) => {
        const entryPath = `${groupPath}[entries][${entryIndex}]`;
        if (entry.id) {
          formData.append(`${entryPath}[id]`, String(entry.id));
        }

        formData.append(`${entryPath}[name]`, entry.name || "");

        const files = Array.isArray(entry.files) ? entry.files : [];
        files.forEach((file, fileIndex) => {
          const filePath = `${entryPath}[files][${fileIndex}]`;
          if (file.id) {
            formData.append(`${filePath}[id]`, String(file.id));
          }

          if (file.file instanceof File) {
            formData.append(`${filePath}[file]`, file.file, file.file.name);
          }
        });
      });
    });

    return formData;
  }

  private entryHasAnyData(entry: PremiumIncomeEntryForm): boolean {
    return !!entry.name?.trim() || entry.existingFiles.length > 0 || entry.newFiles.length > 0;
  }

  private mapResponseGroupToPayload(group: PremiumIncomeGroupResponse): PremiumIncomePayloadGroup {
    const entries = (Array.isArray(group.entries) ? group.entries : [])
      .map((entry) => {
        const files: PremiumIncomePayloadFile[] = (Array.isArray(entry.files) ? entry.files : [])
          .filter((file) => !!file?.id)
          .map((file) => ({ id: file.id }));

        const payloadEntry: PremiumIncomePayloadEntry = {
          name: (entry?.name || "").trim(),
          files
        };

        if (entry?.id) {
          payloadEntry.id = entry.id;
        }

        return payloadEntry;
      })
      .filter((entry) => !!entry.id || !!entry.name || entry.files.length > 0);

    return {
      income_type: group.income_type,
      additional_information: group.additional_information || "",
      entries
    };
  }

  private normalizeEntryName(name: string): string {
    const trimmedName = (name || "").trim();
    if (trimmedName) {
      return trimmedName;
    }

    return this.currentIncomeType === "real_estate" ? "Nieruchomość" : "Przychód kapitałowy";
  }

  private applyLongTitleClass(): void {
    this.entryTitles?.forEach((item) => {
      const titleElement = item.nativeElement;
      titleElement.classList.remove("entry-title--long");
      if (titleElement.clientHeight > 34) {
        titleElement.classList.add("entry-title--long");
      }
    });
  }

  private async validateFile(file: File): Promise<string | null> {
    if (file.type !== "application/pdf") {
      return "PREMIUM_INCOME.ERROR.ONLY_PDF";
    }

    if (file.name && file.name.length > this.maxFileNameLength) {
      return "PREMIUM_INCOME.ERROR.FILE_NAME_TOO_LONG";
    }

    if (file.size / (1024 * 1024) > this.maxFileSizeMb) {
      return "PREMIUM_INCOME.ERROR.MAX_SIZE";
    }

    try {
      const pdfBytes = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pageCount = pdfDoc.getPageCount();

      if (pageCount > this.maxFilePages || pageCount === 0) {
        return "PREMIUM_INCOME.ERROR.MAX_PAGES";
      }
    } catch (_) {
      return "PREMIUM_INCOME.ERROR.INVALID_PDF";
    }

    return null;
  }

  private extractPremiumErrors(errorResponse: any): string[] {
    if (!errorResponse) return [];

    if (Array.isArray(errorResponse)) {
      return errorResponse;
    }

    if (Array.isArray(errorResponse?.premium_income)) {
      return errorResponse.premium_income;
    }

    if (Array.isArray(errorResponse?.spouses) && errorResponse.spouses.length > 0) {
      const spouseError = errorResponse.spouses[0];
      if (Array.isArray(spouseError?.premium_income)) {
        return spouseError.premium_income;
      }
      if (typeof spouseError?.premium_income === "string") {
        return [spouseError.premium_income];
      }
    }

    if (typeof errorResponse?.premium_income === "string") {
      return [errorResponse.premium_income];
    }

    if (Array.isArray(errorResponse?.non_field_errors)) {
      return errorResponse.non_field_errors;
    }

    return [];
  }

  private showErrorSnackbar(message: string): void {
    const config: SnackbarConfig = {
      message,
      duration: 4000,
      type: "ERROR"
    };

    this.store.dispatch({
      type: SHOW_SNACKBAR,
      payload: config
    });
  }

  private get isSpouseMode(): boolean {
    return !!this.spouseId;
  }

  private get activeStatement(): Statement {
    if (this.isSpouseMode && this.statement?.spouse) {
      return this.statement.spouse;
    }
    return this.statement;
  }

  private stepRoute(step: number): string {
    if (this.isSpouseMode) {
      return `/new/statement/${this.statementId}/spouse/${this.spouseId}/additional-options/${step}`;
    }
    return `/new/statement/${this.statementId}/additional-options/${step}`;
  }

  private normalizeSpouseFromResponse(): void {
    if (this.isSpouseMode && this.statement?.spouse && this.statement.spouse.id) {
      this.spouseId = this.statement.spouse.id;
    }
  }

  private appendSpouseBaseFields(formData: FormData, payload: PremiumIncomePayload): void {
    const spouse = this.payloadSpouse;
    const main = this.statement;

    if (!spouse) {
      return;
    }

    formData.append("spouses[0][id]", String(spouse.id || this.spouseId || ""));
    formData.append("spouses[0][first_name]", spouse.firstName || "");
    formData.append("spouses[0][last_name]", spouse.lastName || "");
    formData.append("spouses[0][street]", spouse.street || "");
    formData.append("spouses[0][city]", spouse.city || "");
    formData.append("spouses[0][country]", spouse.country || main?.country || "DE");
    formData.append("spouses[0][postal_code]", spouse.postalCode || "");
    formData.append("spouses[0][house_number]", this.extractHouseNumber(spouse.houseNumberWithSupplement));
    formData.append("spouses[0][house_number_with_supplement]", spouse.houseNumberWithSupplement || "");
    formData.append("spouses[0][statement_year]", String(spouse.year || main?.year || ""));
    formData.append("spouses[0][religion]", String(spouse.religion || ""));
    formData.append("spouses[0][stage_steuer_additional_options]", String(payload.stage_steuer_additional_options));
    formData.append("spouses[0][stage_employers]", String(payload.stage_employers));
    formData.append("spouses[0][has_rental_income]", String(payload.has_rental_income));
    formData.append("spouses[0][has_capital_income]", String(payload.has_capital_income));
  }

  private spousePayload(payload: PremiumIncomePayload): any {
    const spousePayload = this.spouseBasePayload();

    return {
      ...spousePayload,
      stage_steuer_additional_options: payload.stage_steuer_additional_options,
      stage_employers: payload.stage_employers,
      has_rental_income: payload.has_rental_income,
      has_capital_income: payload.has_capital_income,
      premium_income: payload.premium_income
    };
  }

  private spouseBasePayload(): any {
    const spouse = this.payloadSpouse;
    const main = this.statement;
    const spousePayload = spouse?.spouseToResponse("", "summary", main?.filedTogether);

    return {
      ...spousePayload,
      id: spouse?.id || this.spouseId,
      first_name: spouse?.firstName || "",
      last_name: spouse?.lastName || "",
      street: spouse?.street || "",
      city: spouse?.city || "",
      country: spouse?.country || main?.country || "DE",
      postal_code: spouse?.postalCode || "",
      house_number: this.extractHouseNumber(spouse?.houseNumberWithSupplement),
      house_number_with_supplement: spouse?.houseNumberWithSupplement || "",
      statement_year: spouse?.year || main?.year || null,
      religion: spouse?.religion || null,
      deductions: spouse?.hasDeductions && spouse?.deductions ? spouse.deductions.toResponse() : {},
      stage_employers: true,
      premium_income: spouse?.premiumIncome || []
    };
  }

  private extractHouseNumber(houseNumberWithSupplement: string): string {
    const source = (houseNumberWithSupplement || "").trim();
    if (!source) {
      return "";
    }
    const match = source.match(/^\d+/);
    return match ? match[0] : source;
  }

  private get payloadSpouse(): Statement | null {
    if (this.statement?.spouse) {
      return this.statement.spouse;
    }
    if (this.isSpouseMode) {
      return this.activeStatement;
    }
    return null;
  }

  private syncSelectionFromStatement(premiumIncomeGroups: PremiumIncomeGroupResponse[] = []): void {
    const statement = this.activeStatement as any;
    const hasRealEstate = !!statement?.hasRentalIncome || premiumIncomeGroups.some((group) => group?.income_type === "real_estate");
    const hasCapitalIncome = !!statement?.hasCapitalIncome || premiumIncomeGroups.some((group) => group?.income_type === "capital_income");
    const resolvedTypes: PremiumIncomeType[] = [];

    if (hasRealEstate) {
      resolvedTypes.push("real_estate");
    }

    if (hasCapitalIncome) {
      resolvedTypes.push("capital_income");
    }

    if (resolvedTypes.length > 0) {
      this.selectedIncomeTypes = resolvedTypes;
      this.lastResolvedIncomeTypes = resolvedTypes.slice();
      this.syncToolbarButtons();
      return;
    }

    if (!this.isSelectionStep && this.lastResolvedIncomeTypes.length > 0) {
      this.selectedIncomeTypes = this.lastResolvedIncomeTypes.slice();
      this.syncToolbarButtons();
      return;
    }

    this.selectedIncomeTypes = [];
    this.lastResolvedIncomeTypes = [];
    this.syncToolbarButtons();
  }

  private restoreSelectedIncomeTypesIntoStatement(statement: Statement | null): void {
    const selectedTypes = this.getPersistedSelectedIncomeTypes();
    if (!statement || selectedTypes.length === 0) {
      return;
    }

    const activeSpouse = this.isSpouseMode ? statement.spouse : statement;
    if (!activeSpouse) {
      return;
    }

    const premiumIncome = Array.isArray((activeSpouse as any).premiumIncome) ? (activeSpouse as any).premiumIncome : [];
    const premiumIncomeByType = new Map<PremiumIncomeType, PremiumIncomeGroupResponse>();

    premiumIncome.forEach((group) => {
      if (group?.income_type === "real_estate" || group?.income_type === "capital_income") {
        premiumIncomeByType.set(group.income_type, group);
      }
    });

    (activeSpouse as any).premiumIncome = selectedTypes.map((incomeType) => {
      return premiumIncomeByType.get(incomeType) || {
        income_type: incomeType,
        entries: []
      };
    });
    activeSpouse.hasRentalIncome = selectedTypes.includes("real_estate");
    activeSpouse.hasCapitalIncome = selectedTypes.includes("capital_income");
  }

  private persistSelectedIncomeTypes(selectedTypes: PremiumIncomeType[]): void {
    try {
      sessionStorage.setItem(this.getSelectedIncomeTypesStorageKey(), JSON.stringify(selectedTypes || []));
    } catch (_) {}
  }

  private getPersistedSelectedIncomeTypes(): PremiumIncomeType[] {
    try {
      const rawValue = sessionStorage.getItem(this.getSelectedIncomeTypesStorageKey());
      if (!rawValue) {
        return [];
      }

      const parsed = JSON.parse(rawValue);
      return Array.isArray(parsed) ? parsed.filter((value): value is PremiumIncomeType => value === "real_estate" || value === "capital_income") : [];
    } catch (_) {
      return [];
    }
  }

  private getSelectedIncomeTypesStorageKey(): string {
    const statementId = this.statementId || this.statement?.id || 0;
    const spouseId = this.spouseId || this.statement?.spouse?.id || 0;
    return `${this.selectedIncomeTypesStoragePrefix}:${statementId}:${spouseId}`;
  }

  private syncToolbarButtons(): void {
    if (!this.buttons || this.buttons.length < 2) {
      return;
    }

    this.buttons[1].type = this.isSelectionStep && this.getSelectedIncomeTypes().length === 0 ? "skip" : "proceed";
    this.buttons[1].label = this.buttons[1].type === "skip" ? "COMMON.SKIP" : "COMMON.PROCEED";
  }

  private getSelectedIncomeTypes(): PremiumIncomeType[] {
    if (this.selectedIncomeTypes.length > 0) {
      return this.selectedIncomeTypes;
    }

    return [];
  }

  private getNextRouteStep(
    selectedTypes: PremiumIncomeType[] = this.getSelectedIncomeTypes(),
    currentStep: number = this.currentStep
  ): number | undefined {

    if (currentStep === 1) {
      return selectedTypes.length > 0 ? 2 : undefined;
    }

    const currentFormIndex = currentStep - 2;
    if (selectedTypes[currentFormIndex + 1]) {
      return currentStep + 1;
    }

    return undefined;
  }

  private getPreviousRouteStep(): number | undefined {
    if (this.currentStep === 1) {
      return undefined;
    }

    if (this.currentStep === 2) {
      return 1;
    }

    return this.currentStep - 1;
  }
}
